import { PasswordFormType } from '../model/types';
import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'shared/config/store/store';
import { useChangePasswordMutation } from '../api';
import { ApiError } from 'shared/model/types';
import { Button, Drawer, Flex, Form, Input } from 'antd';
import { useIntl } from 'react-intl';
import { useMessage } from 'shared/libs/hooks/useMessage/useMessage';
import { general, generalFields } from 'shared/model/schemas/rules';
import { setActivity } from 'shared/config/store/reducers/appSlice';
import { LockOutlined } from '@ant-design/icons';

type Props = {
    open: boolean;
    onClose: () => unknown;
}

const ChangePasswordDrawer: React.FC<Props> = ({ open, onClose }) => {
    const { formatMessage: t } = useIntl();
    const { showSuccess, showError } = useMessage();
    const [form] = Form.useForm();
    const formRules = {
        oldPassword: [general(t)['required-field']],
        newPassword: [general(t)['required-field'], ...generalFields(t).password],
    }
    const dispatch = useDispatch<AppDispatch>();
    const [changePassword] = useChangePasswordMutation();

    const onSubmit = useCallback(async (data: PasswordFormType) => {
        dispatch(setActivity(true));
        await changePassword({
            oldPassword: data.oldPassword,
            newPassword: data.newPassword
        }).unwrap().then(() => {
            form.resetFields();
            showSuccess?.(t({ id: 'toasts.auth.success.password.title' }));
        }).catch((err: ApiError) => {
            console.log(err);
            showError?.(t({ id: 'toasts.auth.error.password.title' }));
        }).finally(() => {
            dispatch(setActivity(false));
        });
    }, []);

    return (
        <Drawer
            title={t({ id: 'screens.auth.password.title' })}
            open={open}
            closable
            onClose={onClose}
            width={560}
        >
            <Flex vertical align='center' gap={16}>
                <LockOutlined
                    style={{
                        fontSize: 36,
                        padding: 18,
                        width: 'fit-content',
                        boxSizing: 'content-box',
                        background: 'rgba(0, 0, 0, 0.04)',
                        borderRadius: 100,
                        marginBottom: 15,
                    }}
                />
                <Form
                    form={form}
                    onFinish={(e) => onSubmit(e)}
                    className='w100'
                >
                    <Flex vertical align='center'>
                        <Form.Item className='w100' name="oldPassword" rules={formRules.oldPassword}>
                            <Input.Password placeholder={t({ id: 'inputs.password.current' })} />
                        </Form.Item>
                        <Form.Item className='w100' name="newPassword" rules={formRules.newPassword}>
                            <Input.Password placeholder={t({ id: 'inputs.password.new' })} />
                        </Form.Item>
                        <Form.Item className='w100' name="repeatPassword" rules={[
                            {
                                required: true,
                                message: t({ id: 'schemas.new.required-field' }),
                            },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('newPassword') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error(t({ id: 'schemas.password.match' })))
                                }
                            })
                        ]}>
                            <Input.Password placeholder={t({ id: 'inputs.password.confirmPassword' })} />
                        </Form.Item>
                        <Button
                            id="btn__account-name-submit"
                            variant="solid"
                            color='default'
                            onClick={form.submit}>
                            {t({ id: 'buttons.change' })}
                        </Button>
                    </Flex>
                </Form>
            </Flex>
        </Drawer>
    );
};

export default ChangePasswordDrawer;
